import type { SVGProps } from 'react';
import {
  Activity,
  Archive,
  ArrowDownWideNarrow,
  ListFilter,
  ArrowLeft,
  ArrowRight,
  Bell,
  CalendarDays,
  Camera,
  ChartNoAxesColumn,
  ChevronLeft,
  ChevronRight,
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  CircleAlert,
  ClipboardList,
  Clock,
  Columns3,
  ContactRound,
  CreditCard,
  Eye,
  EyeOff,
  FileText,
  Flag,
  GripVertical,
  Inbox,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  ListTodo,
  Lock,
  LogOut,
  MapPin,
  Menu,
  MessageSquare,
  Pause,
  Pencil,
  Play,
  Plus,
  Printer,
  RefreshCw,
  Repeat2,
  RotateCcw,
  RotateCwSquare,
  Rows3,
  ScanLine,
  Search,
  SendHorizontal,
  ShieldCheck,
  SlidersHorizontal,
  Stethoscope,
  StickyNote,
  Tag,
  Target,
  TrendingUp,
  Truck,
  UserRound,
  Users,
  CloudOff,
  X,
  type LucideIcon,
} from 'lucide-react';

/**
 * The icon set: Lucide, one library for every icon in the product. Request of
 * 4 October 2026, section 2.
 *
 * Lucide is SVG drawn on one 24 pixel grid with one stroke, so every icon is
 * sharp at any pixel density and matches its neighbours. Every icon here is
 * drawn with the same stroke width and inherits `currentColor`, so its tone is
 * set by the surrounding rule rather than baked in. Screens import these names
 * and never Lucide itself, so the set is chosen in one file.
 *
 * The brand mark is not an icon but the logo, and stays drawn by hand below.
 */

export type IconProps = SVGProps<SVGSVGElement> & { size?: number };

/** One stroke for the whole set, and the size the prototype's icons used. */
export const ICON_STROKE = 1.75;
const DEFAULT_SIZE = 17;

function icon(Drawn: LucideIcon, defaultSize = DEFAULT_SIZE) {
  return function Icon({ size = defaultSize, ...props }: IconProps) {
    return (
      <Drawn
        size={size}
        strokeWidth={ICON_STROKE}
        absoluteStrokeWidth={false}
        aria-hidden="true"
        focusable="false"
        {...props}
      />
    );
  };
}

/** The overview. */
export const OverviewIcon = icon(LayoutDashboard);

/** A person's own dashboard. The lab's request of 12 October 2026. */
export const DashboardIcon = icon(LayoutGrid);

/** The production line. */
export const LineIcon = icon(Columns3);

/** The line drawn top to bottom. The request of 5 October 2026 (third), section 9. */
export const RowsIcon = icon(Rows3);

/** Orders. */
export const OrdersIcon = icon(ClipboardList);

/** A queue. */
export const QueueIcon = icon(ListTodo);

/** Performance. */
export const PerfIcon = icon(TrendingUp);

/** People. */
export const StaffIcon = icon(Users);

/** The archive. */
export const ArchiveIcon = icon(Archive);

/** The activity log. */
export const ActivityIcon = icon(Activity);

/** Shipping. */
export const ShipIcon = icon(Truck);

/** Payments. */
export const PayIcon = icon(CreditCard);

/** Final Check. */
export const FinalCheckIcon = icon(ShieldCheck);

/** Settings. */
export const SettingsIcon = icon(SlidersHorizontal);

/** The price list. */
export const TagIcon = icon(Tag);

/** Doctors, in the menu. The request of 6 October 2026 (second), item 4. */
export const DoctorIcon = icon(Stethoscope);

/** Patients, in the menu: a record card, so it is not mistaken for the team or a profile. */
export const PatientIcon = icon(ContactRound);

/** Add. */
export const PlusIcon = icon(Plus);

/** Scan a tray. */
export const ScanIcon = icon(ScanLine);

/** Search. */
export const SearchIcon = icon(Search);

/** Notifications. */
export const BellIcon = icon(Bell);

/** Open the menu. */
export const MenuIcon = icon(Menu);

/** Close. */
export const CloseIcon = icon(X);

/** Password. */
export const KeyIcon = icon(KeyRound);

/** Sign out. */
export const SignOutIcon = icon(LogOut);

/** Go on. */
export const GoIcon = icon(ArrowRight, 15);

/** Location. */
export const LocationIcon = icon(MapPin);

/** Back. */
export const GoBackIcon = icon(ArrowLeft);

/** Done. */
export const CheckIcon = icon(Check);

/** Open or close; rotated by the rule that uses it. */
export const ChevronIcon = icon(ChevronDown, 15);

/** Time. */
export const ClockIcon = icon(Clock);

/** A flagged case. */
export const FlagIcon = icon(Flag);

/** The handle a row is dragged by. */
export const GripIcon = icon(GripVertical);

/** Rework. */
export const ReworkIcon = icon(RotateCcw);

/** A remake of a case that came back. The request of 6 October 2026 (second), item 2. */
export const RedoIcon = icon(Repeat2);

/**
 * Turn the enlarged teeth chart a quarter. The request of 5 October 2026
 * (third), section 5. A square turning clockwise, so it is not mistaken for
 * Rework or Refresh, which sit on the same case screen.
 */
export const RotateIcon = icon(RotateCwSquare);

/** A note. */
export const NoteIcon = icon(StickyNote);

/** Start or resume. */
export const PlayIcon = icon(Play);

/** Pause. */
export const PauseIcon = icon(Pause);

/** A photograph. */
export const CameraIcon = icon(Camera);

/**
 * Save a photograph to the device, in the image viewer. The request of
 * 5 October 2026 (third), section 6.
 */
export const DownloadIcon = icon(Download);

/** The photograph before, in the image viewer: a chevron, never an arrow character. */
export const ChevronLeftIcon = icon(ChevronLeft);

/** Edit. */
export const PencilIcon = icon(Pencil);

/** Private. */
export const LockIcon = icon(Lock);

/** Print. */
export const PrintIcon = icon(Printer);

/** Section 9. The not-synced tray in the top bar. */
export const SyncIcon = icon(RefreshCw);

/** Offline: a struck through cloud, not a warning, because offline is normal here. */
export const OfflineIcon = icon(CloudOff);

/** Show a password. Request of 4 October 2026, section 1. */
export const EyeIcon = icon(Eye);

/** Hide a password. */
export const EyeOffIcon = icon(EyeOff);

/** Load the latest. Request of 4 October 2026, section 8. */
export const RefreshIcon = icon(RefreshCw);

/** Lab Notes. Request of 4 October 2026, section 7. */
export const ChatIcon = icon(MessageSquare);

/** Send a note. */
export const SendIcon = icon(SendHorizontal);

/** Collapse. */
export const ChevronUpIcon = icon(ChevronUp, 15);

/** One person. */
export const PersonIcon = icon(UserRound);

/** The tray: a bench's cases waiting, on the tab bar and the empty tray. */
export const TrayIcon = icon(Inbox);

/* The staff report's tiles and controls. Request of 4 October 2026. */
export const CalendarIcon = icon(CalendarDays);
export const SortIcon = icon(ArrowDownWideNarrow);
/** The filters of a list, on the button that opens them. Request of 5 October 2026, section 3. */
export const FilterIcon = icon(ListFilter);
export const ChevronRightIcon = icon(ChevronRight);
export const ChartIcon = icon(ChartNoAxesColumn);
export const TargetIcon = icon(Target);
export const DocumentIcon = icon(FileText);

/** A tooth, which Lucide does not draw: the same grid, stroke and size as the set. */
export function ToothIcon({ size = DEFAULT_SIZE, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path d="M12 5.5c-1.4-1.3-3.2-2-5-1.5C4.4 4.7 3 7.2 3.4 9.9c.3 2 1.2 3.6 1.8 5.4.6 1.9.8 3.9 1.6 5.2.5.8 1.6.7 2-.2.6-1.4.7-3.3 1.4-4.6.6-1.1 2.9-1.1 3.5 0 .7 1.3.8 3.2 1.4 4.6.4.9 1.5 1 2 .2.8-1.3 1-3.3 1.6-5.2.6-1.8 1.5-3.4 1.8-5.4.4-2.7-1-5.2-3.6-5.9-1.8-.5-3.6.2-5 1.5Z" />
    </svg>
  );
}

/** Something needs attention. */
export const AlertIcon = icon(CircleAlert);

/** The logo: drawn by hand, in the brand's gold, and not part of the icon set. */
export function BrandMark({ size = 17 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#d8b53f"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 20V9M10 20V4M16 20v-7M22 20H2" />
    </svg>
  );
}
